import React, { useState } from "react";
import { Button, Checkbox, ColorPicker, Form, Input, Typography, Row, Col } from "antd";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import {
  addEmployeeStatus,
  loadAllEmployeeStatus,
} from "@/redux/rtk/features/hrm/employeeStatus/employeeStatusSlice";

const { Title, Text } = Typography;

export default function AddEmploymentStatus() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [colorCode, setColorCode] = useState("#1677FF");

  const handleSubmit = async (values) => {
    if (loading) return;
    setLoading(true);
    try {
      const payload = {
        ...values,
        colourValue: colorCode,
      };
      const resp = await dispatch(addEmployeeStatus(payload));
      if (resp?.payload?.message === "success") {
        toast.dismiss();
        toast.success("Employment status created successfully!");
        form.resetFields();
        setColorCode("#1677FF");
        dispatch(loadAllEmployeeStatus());
      } else {
        toast.dismiss();
        toast.error("Failed to create employment status");
      }
    } catch {
      toast.dismiss();
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="text-center mb-6 px-2">
        <Title level={3} style={{ marginBottom: 4 }}>
          Create Employment Status
        </Title>
        <Text type="secondary">
          Define how weekends/holidays are paid and set a tag color.
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        name="add-employment-status"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark={false}
        className="px-2 sm:px-6"
      >
        {/* Name + Color (stack on mobile, 2-cols on sm+) */}
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={14}>
            <Form.Item
              label="Status Name"
              name="name"
              rules={[{ required: true, message: "Please input employment status name!" }]}
            >
              <Input
                placeholder="e.g. Permanent, Contractual, Probation"
                size="large"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={10}>
            <Form.Item
              label="Color Code"
              required
              style={{ marginBottom: 0 }}
            // Keep outside form state since you're storing in component state
            >
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                <span className="text-slate-600 text-sm truncate mr-3">{colorCode}</span>
                <ColorPicker
                  showText
                  format="hex"
                  value={colorCode}
                  onChange={(c) => setColorCode(c.toHexString())}
                  presets={[
                    {
                      colors: [
                        "#000000", "#000000E0", "#000000A6", "#00000073", "#00000040",
                        "#00000026", "#0000001A", "#00000012", "#0000000A", "#00000005",
                        "#F5222D", "#FA8C16", "#FADB14", "#8BBB11", "#52C41A",
                        "#13A8A8", "#1677FF", "#2F54EB", "#722ED1", "#EB2F96",
                        "#F5222D4D", "#FA8C164D", "#FADB144D", "#8BBB114D", "#52C41A4D",
                        "#13A8A84D", "#1677FF4D", "#2F54EB4D", "#722ED14D", "#EB2F964D",
                      ],
                    },
                    { label: "Recent", colors: [] },
                  ]}
                />
              </div>
            </Form.Item>
          </Col>
        </Row>

        {/* Checkboxes (wrap nicely on mobile) */}
        <Row gutter={[16, 8]} className="mt-1">
          <Col xs={24} sm={12}>
            <Form.Item name="isWeekendPaid" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Weekend Salary</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="isHolidayPaid" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Holiday Salary</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {/* Description */}
        <Row gutter={[16, 8]} className="mt-3">
          <Col span={24}>
            <Form.Item label="Description" name="description">
              <Input.TextArea placeholder="Optional description" autoSize={{ minRows: 3, maxRows: 6 }} />
            </Form.Item>
          </Col>
        </Row>

        {/* Submit button: full width on mobile, auto on larger screens */}
        <div className="mt-2 flex justify-end">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            className="w-full sm:w-auto"
          >
            Create Employment Status
          </Button>
        </div>
      </Form>
    </>
  );
}
